import { Component, OnDestroy, Optional, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { UserService } from 'app/core/user/user.service';
import { AuthService } from 'app/core/auth/auth.service';
import { UserDto } from 'app/shared/apis/user/model/userDto';
import { TenantDto } from 'app/shared/apis/subscription/model/tenantDto';
import { AppShellService, PersonaRole } from 'app/core/app-shell/app-shell.service';
import { OrganisationNameService } from 'app/shared/services/workspace/organisation-name.service';

type ThemeName = 'safe' | 'bold';

declare global {
    interface Window {
        setTheme?: (theme: ThemeName) => void;
        getTheme?: () => ThemeName;
    }
}

@Component({
    selector     : 'futuristic-layout',
    templateUrl  : './futuristic.component.html',
    // SPEC-meet-ux-004g / GAP-MEETUX-ADMINUI-002: scoped restack of the
    // floating .theme-toggle so it stays on top of any page-leaked overlay
    // from admin-home.component.scss / users.component.scss.
    styleUrls    : ['./futuristic.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FuturisticLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall: boolean;
    user: UserDto;
    selectedTenant: TenantDto;
    currentTenantRoles: string[];
    currentRole: PersonaRole = 'is';

    // GAP-MEETUX-004b: Safe/Bold theme toggle on the shared role shell.
    // Mirrors the canonical pattern from LandingHomeComponent; theme state and
    // persistence are owned by assets/js/theme.js (window.setTheme/getTheme).
    currentTheme: ThemeName = 'safe';

    // SESSION-webplanner-004 / GAP-WEBPLANNER-004: the floating SAFE/BOLD
    // toggle UI is hidden for now per Prashant. The theme infrastructure
    // stays intact (tokens, [data-theme="bold"] rules in molecules.css /
    // organisms.css, window.setTheme API) — CLAUDE.md hard rule 8 still
    // applies. Flip this to `true` to surface the toggle when needed.
    showThemeToggle = false;
    private _onThemeChanged = (e: Event): void => {
        const detail = (e as CustomEvent<{ theme: ThemeName }>).detail;
        if (detail?.theme) {
            this.currentTheme = detail.theme;
        }
    };

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _userService: UserService,
        private _authService: AuthService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _appShellService: AppShellService,
        @Optional() private _orgNames?: OrganisationNameService
    )
    {
        this.user = this._authService.user;
        this.selectedTenant = _authService.selectedTenant;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    /**
     * SESSION-meet-ux-004i / SIDEBAR-001 (AC-10 / AC-13). Map the current router
     * URL to the persona nav id consumed by <app-app-shell>'s ROLES_MAP, and
     * pass it down via [activeNav]. PD-2: manual matching, NOT routerLinkActive.
     *
     * Exact matches are tried first so the parent route `/admin` highlights
     * Overview ONLY and never double-highlights with `/admin/*` children — the
     * same fix the SESSION-004h `exactMatch: true` patch applied to the Fuse
     * mock-nav, now baked into the canonical shell. Prefix fallbacks (ordered by
     * specificity) cover nested feature routes that don't have an exact entry.
     *
     * A plain getter is sufficient: this layout uses default change detection,
     * so it re-evaluates on every Router navigation — no NavigationEnd
     * subscription / markForCheck needed.
     */
    get activeNavId(): string
    {
        const url = this._router.url.split('?')[0].split('#')[0];

        // Exact matches first — prevents `/admin` from matching `/admin/teams`.
        const exact: Record<string, string> = {
            '/admin':          'overview',
            '/admin/users':    'users',
            // SPEC-web-planner-035 AC-4 — Admin Workspace settings.
            '/admin/settings': 'settings',
            // SESSION-admin-ptg-004 — Teams & Groups lives at /teams (not /admin/teams).
            '/teams':          'teams',
            // SESSION-meet-ux-017 (Phase 1) — IS Home active-nav entry.
            '/home':           'home',
            // SESSION-meet-ux-017 (Phase 2 visual pass) — Activity entry.
            '/activity':       'activity',
            // SESSION-webplanner-002 — New session sub-item under My Sessions
            // (proto §3.1). Must come BEFORE the `/sessions` prefix check so
            // /sessions/new highlights the child, not the parent.
            '/sessions/new':   'new-session',
            // SPEC-web-planner-024 AC-6 — IS sidebar Getting started entry.
            '/getting-started': 'getting-started',
            '/queue':          'queue',
            '/deals':          'deals',
            '/portfolio':      'portfolio',
            '/reps':           'reps',
            '/forecast':       'forecast',
            '/coaching':       'coaching',
            '/products':       'products',
        };
        if ( exact[url] )
        {
            return exact[url];
        }

        // Prefix matches for nested routes — order by specificity.
        if ( url.startsWith('/accounts') ) { return 'accounts'; }
        if ( url.startsWith('/contacts') ) { return 'contacts'; }
        if ( url.startsWith('/sessions') ) { return 'sessions'; }
        // SPEC-meet-ux-019 / GAP-MEET-UX-003 — /queue/:oppId (Qualification
        // review page) highlights the same sidebar entry as the /queue list.
        if ( url.startsWith('/queue') )    { return 'queue'; }
        return '';
    }

    // ── Bottom-sidebar persona block (GAP-MEETUX-CONTACTUI-015 + post-deploy
    // diff vs proto `Webplanner _Remix/assets/js/app-shell.js:113-120`).
    // Replaces the broken `<user>` matMenu (mismatched photoUrl/avatar property
    // bindings + legacy Fuse "Signed in / Settings / Sign out" popup) with a
    // single proto-matching click target → /user-settings.

    /** Avatar palette class for the persona — `avatar-{amber|coral|purple|teal}`. */
    get personaTint(): 'amber' | 'coral' | 'purple' | 'teal' {
        switch (this.currentRole) {
            case 'admin': return 'amber';
            case 'fs':    return 'coral';
            case 'cro':   return 'teal';
            default:      return 'purple';
        }
    }

    /** Canonical sentence-case role label matching the proto's `role.label`. */
    get roleLabel(): 'Admin' | 'Inside Sales' | 'Field Sales' | 'CRO' {
        switch (this.currentRole) {
            case 'admin': return 'Admin';
            case 'fs':    return 'Field Sales';
            case 'cro':   return 'CRO';
            default:      return 'Inside Sales';
        }
    }

    /** Two-letter initials for the avatar — mirrors admin-home's orgMark logic. */
    get userInitials(): string {
        const first = this.user?.firstName?.trim() ?? '';
        const last = this.user?.lastName?.trim() ?? '';
        const fromParts = `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
        if (fromParts.length === 2) return fromParts;
        const composed = (this.user?.displayName ?? `${first} ${last}`).trim();
        const parts = composed.split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    /** "Pierre Desjardins" — display name with sensible fallbacks. */
    get userDisplayName(): string {
        const first = this.user?.firstName?.trim() ?? '';
        const last = this.user?.lastName?.trim() ?? '';
        const composed = `${first} ${last}`.trim();
        return composed || this.user?.displayName || '';
    }

    /** Workspace org name for the subtitle — e.g. "Prognosys Medical". Falls
     * back through the same cascade admin-home uses, then to the role's
     * canonical territory (`Canada` for CRO, the proto's territory string for
     * personal personas) if no workspace name is available. */
    get workspaceLabel(): string {
        // The same source as the shell and the pages (OrganisationNameService), so a rename shows at once;
        // this.selectedTenant is a constructor-time copy.
        const full = this._orgNames?.name() ?? this.selectedTenant?.organization?.trim();
        if (full) return full;
        const compactName = (this.user?.tenants ?? [])
            .find((t: any) => t?.id === this.selectedTenant?.id)?.name?.trim();
        if (compactName) return compactName;
        // Proto fallback: territory string from the role config.
        switch (this.currentRole) {
            case 'cro':   return 'Canada';
            case 'admin': return 'Workspace';
            default:      return 'Ontario-West';
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this._authService.user$.subscribe(user => this.user = user);

        this.user.tenants.forEach((tenantCompact) => {
            if (tenantCompact.id === this.selectedTenant.id) {
                this.currentTenantRoles = tenantCompact.roles;
            }
        });

        // Get current role and wire accent colors
        this.currentRole = this._appShellService.getPersonaRole();
        this.wireAccentColors();

        // GAP-MEETUX-004b: reflect the persisted theme and stay in sync when the
        // theme is changed elsewhere (e.g. /home toggle or console setTheme()).
        this.currentTheme = (window.getTheme?.() as ThemeName) || 'safe';
        window.addEventListener('theme-changed', this._onThemeChanged);

        // Subscribe to route changes to update role
        this._activatedRoute.params
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.currentRole = this._appShellService.getPersonaRole();
                this.wireAccentColors();
            });

        // SESSION-meet-ux-004i / SIDEBAR-001: the Fuse `navigation$` subscription
        // that fed `<fuse-vertical-navigation>` per-persona arrays is gone — the
        // canonical <app-app-shell> owns its own ROLES_MAP and resolves the
        // persona from `[role]="currentRole"` (set above + on route change).

        // Subscribe to the user service
        // this._userService.user$
        //     .pipe((takeUntil(this._unsubscribeAll)))
        //     .subscribe((user: User) => {
        //         this.user = user;
        //         this.user.email = JSON.parse(localStorage.getItem('user')).email;
        //     });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        window.removeEventListener('theme-changed', this._onThemeChanged);
        this.resetAccentColors();
    }

    /**
     * Safe/Bold theme switch (GAP-MEETUX-004b). Delegates to the canonical
     * window.setTheme() from assets/js/theme.js, which flips
     * <html data-theme="..."> and persists to localStorage. We only mirror the
     * value locally for the toggle's active-button styling — the theme-changed
     * event keeps it in sync if the theme is set from another surface.
     */
    setTheme(theme: ThemeName): void {
        window.setTheme?.(theme);
        this.currentTheme = theme;
    }

    // DESIGN-Q: OD-8 — Sidebar color binding ambiguity
    // This method sets sidebar color based on currentRole. But should the sidebar color
    // be bound to the user's BACKEND role (fixed at login) or the CURRENT ROUTE's role (dynamic)?
    // For design verification, we're using route-based color. In production, tie to backend role only.
    wireAccentColors(): void {
        const root = document.documentElement;
        const accentMap: Record<PersonaRole, string> = {
            'is':    'var(--purple)',
            'fs':    'var(--coral)',
            'cro':   'var(--teal)',
            'admin': 'var(--amber)'
        };

        const accent = accentMap[this.currentRole] || accentMap['is'];
        root.style.setProperty('--accent', accent);
        root.style.setProperty('--accent-fg', `var(--${this.getColorPrefix()}-d)`);
        root.style.setProperty('--accent-soft', `var(--${this.getColorPrefix()}-l)`);
    }

    resetAccentColors(): void {
        const root = document.documentElement;
        root.style.removeProperty('--accent');
        root.style.removeProperty('--accent-fg');
        root.style.removeProperty('--accent-soft');
    }

    /**
     * Open the user settings page from the bottom sidebar user button
     * (CONTACTUI-015). The avatar still opens the <user> matMenu for Sign out;
     * clicking the row goes straight to settings.
     */
    openUserSettings(): void {
        this._router.navigate(['/user-settings']);
    }

    // Sidebar background is now the dark-navy shell (`appshell-sidebar` class →
    // --navy-800), not the role accent — see SPEC-008d / GAP-MEETUX-CONTACTUI-015.
    // The role colour is applied as --accent only, via wireAccentColors().

    private getColorPrefix(): string {
        const prefixMap: Record<PersonaRole, string> = {
            'is':    'purple',
            'fs':    'coral',
            'cro':   'teal',
            'admin': 'amber'
        };
        return prefixMap[this.currentRole] || 'purple';
    }

    getLibraryNavItems(): Array<{ label: string; icon: string }> {
        return [
            { label: 'Contacts', icon: 'users' },
            { label: 'Products', icon: 'package' },
            { label: 'My Sessions', icon: 'video' }
        ];
    }
}
